import { lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import Layout from './components/Layout'
import RequireAuth from './auth/RequireAuth'
import RequireTenantAdmin from './auth/RequireTenantAdmin'
import RequireSuperAdmin from './auth/RequireSuperAdmin'

const Login = lazy(()=> import('./pages/Login'))
const ForgotPassword = lazy(()=> import('./pages/ForgotPassword'))
const ResetPassword = lazy(()=> import('./pages/ResetPassword'))
const CreateOrganization = lazy(()=> import('./pages/CreateOrganization'))
const AcceptInvitation = lazy(()=> import('./pages/AcceptInvitation'))

// Admin Pages
const TenantUsers = lazy(()=> import('./pages/admin/TenantUsers'))
const AllUsers = lazy(()=> import('./pages/admin/AllUsers'))
const AdminPlans = lazy(()=> import('./pages/admin/Plans'))
const AdminTenants = lazy(()=> import('./pages/admin/Tenants'))
const SystemSettings = lazy(()=> import('./pages/admin/SystemSettings'))
const TenantSettings = lazy(()=> import('./pages/admin/TenantSettings'))
const Analytics = lazy(()=> import('./pages/admin/Analytics'))
const Integrations = lazy(()=> import('./pages/admin/Integrations'))

// Feature Pages
const BrainDump = lazy(()=> import('./pages/BrainDump'))
const Todos = lazy(()=> import('./pages/Todos'))
const Habits = lazy(()=> import('./pages/Habits'))
const Matrix = lazy(()=> import('./pages/Matrix'))
const Calendar = lazy(()=> import('./pages/Calendar'))
const Expenses = lazy(()=> import('./pages/Expenses'))
const Feynman = lazy(()=> import('./pages/Feynman'))
const Pomodoro = lazy(()=> import('./pages/Pomodoro'))
const Journal = lazy(()=> import('./pages/Journal'))
const Goals = lazy(()=> import('./pages/Goals'))
const Kanban = lazy(()=> import('./pages/Kanban'))
const Flashcards = lazy(()=> import('./pages/Flashcards'))
const Reading = lazy(()=> import('./pages/Reading'))
const TimeBlocking = lazy(()=> import('./pages/TimeBlocking'))

// Family Pages
const FamilyHub = lazy(()=> import('./pages/family/FamilyHub'))
const FamilyTodos = lazy(()=> import('./pages/family/FamilyTodos'))
const FamilyCalendar = lazy(()=> import('./pages/family/FamilyCalendar'))
const FamilyKanban = lazy(()=> import('./pages/family/FamilyKanban'))
const FamilyGoals = lazy(()=> import('./pages/family/FamilyGoals'))
const FamilyExpenses = lazy(()=> import('./pages/family/FamilyExpenses'))

// Subscription Pages
const Plans = lazy(()=> import('./pages/Plans'))
const CurrentPlan = lazy(()=> import('./pages/CurrentPlan'))
const SubscriptionSuccess = lazy(()=> import('./pages/SubscriptionSuccess'))

// Legal Pages
const TermsOfService = lazy(()=> import('./pages/legal/TermsOfService'))
const PrivacyPolicy = lazy(()=> import('./pages/legal/PrivacyPolicy'))
const CookiePolicy = lazy(()=> import('./pages/legal/CookiePolicy'))

export const routerConfig = createBrowserRouter([
  { path: '/login',                element: <Login/> },
  { path: '/forgot-password',       element: <ForgotPassword/> },
  { path: '/reset-password',        element: <ResetPassword/> },
  { path: '/create-organization',   element: <CreateOrganization/> },
  { path: '/accept-invitation',     element: <AcceptInvitation/> },
  { path: '/terms',                 element: <TermsOfService/> },
  { path: '/privacy',               element: <PrivacyPolicy/> },
  { path: '/cookies',               element: <CookiePolicy/> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <BrainDump/> },
      { path: 'todos', element: <Todos/> },
      { path: 'habits', element: <Habits/> },
      { path: 'matrix', element: <Matrix/> },
      { path: 'calendar', element: <Calendar/> },
      { path: 'expenses', element: <Expenses/> },
      { path: 'feynman', element: <Feynman/> },
      { path: 'pomodoro', element: <Pomodoro/> },
      { path: 'journal', element: <Journal/> },
      { path: 'goals', element: <Goals/> },
      { path: 'kanban', element: <Kanban/> },
      { path: 'flashcards', element: <Flashcards/> },
      { path: 'reading', element: <Reading/> },
      { path: 'time-blocking', element: <TimeBlocking/> },
      { path: 'family', element: <FamilyHub/> },
      { path: 'family/todos', element: <FamilyTodos/> },
      { path: 'family/calendar', element: <FamilyCalendar/> },
      { path: 'family/kanban', element: <FamilyKanban/> },
      { path: 'family/goals', element: <FamilyGoals/> },
      { path: 'family/expenses', element: <FamilyExpenses/> },
      { path: 'plans', element: <Plans/> },
      { path: 'current-plan', element: <CurrentPlan/> },
      { path: 'subscription/success', element: <SubscriptionSuccess/> },
      
      // ── SuperAdmin Routes ──
      // Justification: System-wide management requires highest privilege level
      {
        path: 'admin/all-users',
        element: (
          <RequireSuperAdmin>
            <AllUsers/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: 'admin/plans',
        element: (
          <RequireSuperAdmin>
            <AdminPlans/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: 'admin/tenants',
        element: (
          <RequireSuperAdmin>
            <AdminTenants/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: 'admin/settings',
        element: (
          <RequireSuperAdmin>
            <SystemSettings/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: 'admin/analytics',
        element: (
          <RequireSuperAdmin>
            <Analytics/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: 'admin/integrations',
        element: (
          <RequireSuperAdmin>
            <Integrations/>
          </RequireSuperAdmin>
        ),
      },
      
      // ── TenantAdmin Routes ──
      // Justification: Tenant-scoped management, accessible to both TenantAdmin and SuperAdmin
      {
        path: 'admin/users',
        element: (
          <RequireTenantAdmin>
            <TenantUsers/>
          </RequireTenantAdmin>
        ),
      },
      {
        path: 'admin/tenant-settings',
        element: (
          <RequireTenantAdmin>
            <TenantSettings/>
          </RequireTenantAdmin>
        ),
      },
      {
        path: 'admin/tenants/:tenantId/users',
        element: (
          <RequireSuperAdmin>
            <TenantUsers/>
          </RequireSuperAdmin>
        ),
      },
    ]
  }
])